import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlarmClock,
  Bag,
  Bed,
  CashFunds,
  CheckOutline,
  ChevronDown,
  Clock,
  CloseOutline,
  CustomCard,
  Design,
  Envelope,
  ExternalRegistry,
  Gift,
  GuestList,
  HotelProduct,
  Invitation,
  LockFilled,
  MailingAddress,
  Moon,
  PageManager,
  PhotoGallery,
  Plane,
  Rsvp,
  SaveTheDate,
  Send,
  Shipping,
  Star,
  Stationery,
  Sun,
  Venue,
  WebGlobe
} from '@withjoy/joykit/icons';
import { DashboardStat, Stat } from '@apps/admin/routes/DashboardPreview/components/DashboardChecklist/components/DashboardStats';
import { AccordionMenu, AccordionMenuItemProps } from './components/AccordionMenu/AccordionMenu';
import { AccordionItem, AccordionItemProps } from './components/AccordionItem';
import { StatusBadge, StatusBadgeItemProps } from './components/StatusBadge';
import { EventPageVisibility, useGetDashboardAccordionDataQuery, useGetRegistryOrdersIdsForAccordionQuery } from '@graphql/generated';
import { getLocalStorage, useRouterHelper, useTranslation } from '@shared/core';
import { DashboardAccordionWrapper, styles } from './DashboardAccordion.styles';
import { useTransition, animated } from 'react-spring';
import { SettingsRoutes } from '@apps/admin/routes/Settings/Settings.routes';
import { routePaths as adminRoutePaths, monorepoRoutePaths as adminMonorepoRoutePaths } from '@apps/admin/route.paths';
import { PageListDialog } from './components/PageListDialog';
import { ButtonV2, useDisclosure } from '@withjoy/joykit';
import { EventPageSlugs } from '@apps/admin/routes/Dashboard/common/enums/EventPageSlugs';
import { PageSettingsDialogProvider } from '@apps/registry/admin/routes/YourRegistry/components/PageSettingsDialog/PageSettingsDialog.provider';
import { routePaths as registryRoutePaths } from '@apps/registry/admin/Registry.routes';
import { useIntercom } from '@shared/core/intercom';
import { useFeatureValue } from '@shared/core/featureFlags';
import { PromoCard, PromoCardProps } from './components/PromoCard';
import { cubicBezierEasingFn } from '@shared/utils/animationTransition';
import { useDashboardAccordionTelemetry } from './DashboardAccordion.telemetry';

export type AccordionItem = Pick<AccordionItemProps, 'icon' | 'accentColor' | 'showBadge' | 'badgeLabel' | 'isEnabled'> & {
  id: 'website' | 'planningRsvp' | 'saveTheDates' | 'registry' | 'travel' | 'invitations' | 'contactGuests';
  promoCard?: PromoCardProps;
  statusBadgeItems?: Array<StatusBadgeItemProps & { link?: string; pageSlug?: EventPageSlugs }>;
  dashboardStats?: Stat[][];
  accordionMenuItems: Array<
    Omit<AccordionMenuItemProps, 'label'> & {
      id:
        | 'website'
        | 'photos'
        | 'hotels'
        | 'editPages'
        | 'guestList'
        | 'contactCollector'
        | 'manageRSVP'
        | 'giftsAndDecor'
        | 'designAndSend'
        | 'overview'
        | 'addGifts'
        | 'manage'
        | 'trackAndThanks'
        | 'roomBlocks'
        | 'travelPage'
        | 'whereToStay'
        | 'sendMessageToGuests'
        | 'remindGuestsToRSVP'
        | 'sendBookHotelReminder'
        | 'thankYouCards'
        | 'customCard'
        | 'websiteOverview';
    }
  >;
};

const PROMO_CARDS_VISIBILITY_STORAGE_KEY = 'adminDashboardV3PromoCardsVisibility';
const localStorage = getLocalStorage();

export const DashboardAccordion: React.FC<{ eventHandle: string; eventId: string; onProductSelectionOpen: () => void }> = ({ eventHandle, eventId, onProductSelectionOpen }) => {
  const { t, t2 } = useTranslation('adminDashboardV3');
  const translationsFns = t('dashboardAccordion');
  const translations = t2('dashboardAccordion');

  const telemetry = useDashboardAccordionTelemetry();

  const [promoCardsVisibility, setPromoCardsVisibility] = useState<Record<AccordionItem['id'], boolean> | null>(null);

  const { buildPath } = useRouterHelper();
  const intercom = useIntercom();
  const isHotelBlockEnabled = useFeatureValue('hotelBlockEnabled').value === 'on';
  const adminWebsiteOverviewExperimentEnabled = useFeatureValue('adminWebsiteOverviewExperiment').value === 'treatment';
  const photosCollectionExperimentEnabled = useFeatureValue('photosCollectionExperiment').value === 'treatment';

  const { isOpen: isPageListDialogOpen, onToggle: onPageListDialogToggle, onClose: onPageListDialogClose } = useDisclosure();

  useEffect(() => {
    const visibility = localStorage.getItem(PROMO_CARDS_VISIBILITY_STORAGE_KEY);
    setPromoCardsVisibility(visibility ? (JSON.parse(visibility) as Record<AccordionItem['id'], boolean>) : null);
  }, []);

  const handlePromoCardVisibilityChange = useCallback((id: AccordionItem['id'], isVisible: boolean) => {
    setPromoCardsVisibility(prev => {
      const updatedVisibility = prev ? { ...prev, [id]: isVisible } : ({ [id]: isVisible } as Record<AccordionItem['id'], boolean>);
      localStorage.setItem(PROMO_CARDS_VISIBILITY_STORAGE_KEY, JSON.stringify(updatedVisibility));
      return updatedVisibility;
    });
  }, []);

  const { loading: loadingRegistryOrdersIds, data: registryOrdersIds } = useGetRegistryOrdersIdsForAccordionQuery({
    variables: { eventHandle },
    batchMode: 'fast'
  });
  const { loading: accordionDataLoading, data: accordionData } = useGetDashboardAccordionDataQuery({
    variables: {
      eventHandle,
      eventId,
      getEcardsByEventIdId: eventId,
      args: {
        externalMessageIds: registryOrdersIds?.eventByName?.registry.orders?.length ? registryOrdersIds?.eventByName?.registry.orders?.map(order => order.id) : [''],
        relationshipTag: 'GIFT_TRACKER_THANK_YOU'
      }
    },
    batchMode: 'fast',
    skip: loadingRegistryOrdersIds || !eventId,
    onCompleted: () => telemetry.accordionDataLoaded()
  });

  const handleAccordionItemToggleClick = useCallback(
    (isOpen: boolean, accordionId: AccordionItem['id']) => {
      telemetry.toggleAccordionItem(isOpen, accordionId);
    },
    [telemetry]
  );

  const handleAccordionBadgeClick = useCallback(
    (accordionId: AccordionItem['id']) => {
      telemetry.clickAccordionToggleBadge(accordionId);
      onProductSelectionOpen();
    },
    [onProductSelectionOpen, telemetry]
  );

  const handlePromoCardPrimaryCtaClick = useCallback(
    (accordionId: AccordionItem['id']) => {
      telemetry.clickPromoCardPrimaryCta(accordionId);
      handlePromoCardVisibilityChange(accordionId, false);
    },
    [handlePromoCardVisibilityChange, telemetry]
  );

  const handlePromoCardSecondaryCtaClick = useCallback(
    (accordionId: AccordionItem['id']) => {
      telemetry.clickPromoCardSecondaryCta(accordionId);
      if (accordionId === 'website') {
        onPageListDialogToggle();
        return;
      }
      handlePromoCardVisibilityChange(accordionId, false);
    },
    [handlePromoCardVisibilityChange, onPageListDialogToggle, telemetry]
  );

  const handlePromoCardDismissClick = useCallback(
    (id: AccordionItem['id']) => {
      telemetry.clickPromoCardDismiss(id);
      handlePromoCardVisibilityChange(id, false);
    },
    [handlePromoCardVisibilityChange, telemetry]
  );

  const handleMenuItemClick = useCallback(
    (menuItemId: AccordionItem['accordionMenuItems'][number]['id']) => {
      telemetry.clickAccordionMenuItem(menuItemId);
      if (menuItemId === 'editPages') {
        onPageListDialogToggle();
        return;
      }
    },
    [onPageListDialogToggle, telemetry]
  );

  const handleStatusBadgeItemClick = useCallback(
    (itemId: AccordionItem['id']) => {
      telemetry.clickAccordionStatusBadge(itemId);
    },
    [telemetry]
  );

  const handleStatClick = useCallback(
    (statId: Stat['id']) => {
      telemetry.clickAccordionStat(statId);
      if (statId === 'roomsRequested' && intercom) {
        intercom.show();
      }
    },
    [intercom, telemetry]
  );

  const getStatusBadgeItemLabel = useCallback(
    (pageVisibility?: EventPageVisibility, pageLabel?: string) => {
      if (!pageVisibility || !pageLabel) return '';

      switch (pageVisibility) {
        case EventPageVisibility.public:
          return translationsFns.displayStatus.isPublic({ page: pageLabel });
        case EventPageVisibility.passwordProtected:
          return translationsFns.displayStatus.isProtected({ page: pageLabel });
        case EventPageVisibility.hidden:
          return translationsFns.displayStatus.isOff({ page: pageLabel });
      }
    },
    [translationsFns.displayStatus]
  );

  const getStatusBadgeIcon = useCallback((pageVisibility?: EventPageVisibility) => {
    if (!pageVisibility) return null;

    switch (pageVisibility) {
      case EventPageVisibility.public:
        return <Sun size={'sm'} />;
      case EventPageVisibility.passwordProtected:
        return <LockFilled size={'sm'} />;
      case EventPageVisibility.hidden:
        return <Moon size={'sm'} />;
    }
  }, []);

  const accordionItems = useMemo<AccordionItem[]>(() => {
    if (!accordionData) return [];

    const pages = {
      public: accordionData?.eventByName?.pages.filter(page => page.visibility === EventPageVisibility.public).length || 0,
      passwordProtected: accordionData?.eventByName?.pages.filter(page => page.visibility === EventPageVisibility.passwordProtected).length || 0,
      hidden: accordionData?.eventByName?.pages.filter(page => page.visibility === EventPageVisibility.hidden).length || 0
    };

    const printSaveTheDates = accordionData.stationeryPrintOrdersByEvent.orders
      .filter(order => order.cardDraft?.stationeryTemplate.category === 'saveTheDate')
      .reduce((acc, order) => acc + order.quantity, 0);

    const printInvitations = accordionData.stationeryPrintOrdersByEvent.orders
      .filter(order => order.cardDraft === null || order.cardDraft?.stationeryTemplate.category === 'invitation')
      .reduce((acc, order) => acc + order.quantity, 0);

    const cashReceivedInMinorUnits =
      accordionData.eventByName?.registry.items.reduce(
        (acc, item) => acc + (item.donationFund?.donations ? item.donationFund.donations.reduce((acc, donation) => acc + donation.amount.valueInMinorUnits, 0) : 0),
        0
      ) || 0;

    const goalInMinorUnits = accordionData.eventByName?.registry.items.reduce((acc, item) => acc + (item.donationFund?.goalMonetaryValue?.valueInMinorUnits || 0), 0);

    const rsvpStats = accordionData.eventByName?.guests.people.reduce(
      (acc, guest) => {
        const rsvpQuestion = guest.legacyRsvpAnswers?.find(answer => answer?.questionId === 'rsvp');
        if (rsvpQuestion?.answer === 'true') {
          acc.attending += 1;
        }
        if (rsvpQuestion?.answer === 'false') {
          acc.declined += 1;
        }
        if (rsvpQuestion?.answer === null) {
          acc.pending += 1;
        }
        return acc;
      },
      { attending: 0, declined: 0, pending: 0 }
    );

    const items: AccordionItem[] = [
      {
        id: 'website',
        accentColor: 'accentBlue3',
        icon: <WebGlobe />,
        isEnabled: !accordionData.eventByName?.productVerticals?.optOutOfWebsite,
        showBadge: true,
        promoCard: {
          title: translations.promoCards.website.title,
          subtitle: translations.promoCards.website.subtitle,
          showDismissButton: true,
          backgroundImageUrl: 'https://withjoy.com/assets/public/dashboard/v3-promo-cards/promo-cards-website.jpg',
          innerTopHeight: 240,
          isVisible: promoCardsVisibility?.website !== false,
          primaryCtaLabel: translations.promoCards.website.primaryCta,
          primaryCtaLink: buildPath(adminRoutePaths.design.path),
          renderSecondaryCta: () => (
            <ButtonV2 size="lg" variant="outline" shape="rounded" intent="neutral" fullWidth onClick={() => handlePromoCardSecondaryCtaClick('website')} endIcon={<ChevronDown />}>
              {translations.promoCards.website.secondaryCta}
            </ButtonV2>
          )
        },
        ...(!accordionData.eventByName?.productVerticals?.optOutOfWebsite && {
          statusBadgeItems: [
            { label: translationsFns.displayStatus.publicPage({ count: pages.public }), icon: <Sun size={'sm'} />, link: buildPath(SettingsRoutes.website) },
            {
              label: translationsFns.displayStatus.protectedPage({ count: pages.passwordProtected }),
              icon: <LockFilled size={'sm'} />,
              link: buildPath(SettingsRoutes.website)
            },
            { label: translationsFns.displayStatus.off({ count: pages.hidden }), icon: <Moon size={'sm'} />, link: buildPath(SettingsRoutes.website) }
          ]
        }),
        accordionMenuItems: [
          ...(adminWebsiteOverviewExperimentEnabled
            ? [{ id: 'websiteOverview' as const, icon: <WebGlobe size={'xl'} />, link: buildPath(adminRoutePaths.websiteOverview.path) }]
            : []),
          { id: 'website', icon: <Design size={'xl'} />, link: buildPath(adminRoutePaths.design.path) },
          {
            id: 'photos',
            icon: <PhotoGallery size={'xl'} />,
            link: photosCollectionExperimentEnabled ? buildPath(adminRoutePaths.photoCollection.path) : buildPath(adminRoutePaths.photos.path)
          },
          { id: 'hotels', icon: <HotelProduct size={'xl'} />, isNew: true, link: buildPath(adminRoutePaths.accommodations.path) },
          { id: 'editPages', icon: <PageManager size={'xl'} />, showChevronDown: true }
        ]
      },
      {
        id: 'planningRsvp',
        accentColor: 'accentBlue4',
        icon: <GuestList />,
        isEnabled: !accordionData.eventByName?.productVerticals?.optOutOfGuestList,
        showBadge: true,
        badgeLabel: translations.badges.rsvpIs,
        promoCard: {
          title: translations.promoCards.planningRsvp.title,
          subtitle: translations.promoCards.planningRsvp.subtitle,
          showDismissButton: true,
          backgroundImageUrl: 'https://withjoy.com/assets/public/dashboard/v3-promo-cards/promo-cards-planning-rsvp.jpg',
          innerTopHeight: 140,
          isVisible: promoCardsVisibility?.planningRsvp !== false,
          primaryCtaLabel: translations.promoCards.planningRsvp.primaryCta,
          primaryCtaLink: buildPath(adminRoutePaths.contact.path),
          secondaryCtaLabel: translations.promoCards.planningRsvp.secondaryCta,
          secondaryCtaLink: buildPath('/app')
        },
        ...(!accordionData.eventByName?.productVerticals?.optOutOfGuestList && {
          statusBadgeItems: [
            {
              label: getStatusBadgeItemLabel(accordionData.eventByName?.pages?.find(page => page.pageSlug === EventPageSlugs.Rsvp)?.visibility, translations.badges.rsvp),
              icon: getStatusBadgeIcon(accordionData.eventByName?.pages?.find(page => page.pageSlug === EventPageSlugs.Rsvp)?.visibility),
              pageSlug: EventPageSlugs.Rsvp
            }
          ]
        }),
        ...(rsvpStats?.attending && {
          dashboardStats: [
            [
              {
                id: 'potentialGuests',
                value: accordionData.eventByName?.guests.peopleCount || 0,
                type: 'default',
                icon: <GuestList />,
                link: buildPath(adminRoutePaths.guests.path)
              },
              {
                id: 'contactsCollected',
                value: accordionData.getContactCollectionByEventId?.contacts?.length || 0,
                type: 'default',
                icon: <MailingAddress />,
                link: buildPath(adminRoutePaths.contact.path)
              }
            ],
            [
              { id: 'attending', value: rsvpStats?.attending || 0, type: 'default', icon: <CheckOutline />, link: buildPath(adminRoutePaths.guests.path) },
              { id: 'declined', value: rsvpStats?.declined || 0, type: 'default', icon: <CloseOutline />, link: buildPath(adminRoutePaths.guests.path) },
              { id: 'pending', value: rsvpStats?.pending || 0, type: 'default', icon: <Clock />, link: buildPath(adminRoutePaths.guests.path) }
            ]
          ]
        }),
        accordionMenuItems: [
          { id: 'guestList', icon: <GuestList size={'xl'} />, link: buildPath(adminRoutePaths.guests.path) },
          { id: 'contactCollector', icon: <MailingAddress size={'xl'} />, link: buildPath(adminRoutePaths.contact.path) },
          { id: 'manageRSVP', icon: <Rsvp size={'xl'} />, link: buildPath(adminRoutePaths.rsvp.path) },
          { id: 'giftsAndDecor', icon: <Venue size={'xl'} />, isNew: true, link: 'https://weddingshop.withjoy.com/' }
        ]
      },
      {
        id: 'saveTheDates',
        accentColor: 'accentGreen4',
        icon: <SaveTheDate />,
        showBadge: true,
        isEnabled: !accordionData.eventByName?.productVerticals?.optOutOfInvites,
        promoCard: {
          title: translations.promoCards.saveTheDates.title,
          subtitle: translations.promoCards.saveTheDates.subtitle,
          showDismissButton: true,
          backgroundImageUrl: 'https://withjoy.com/assets/public/dashboard/v3-promo-cards/savethedate_dash_card@2x.jpg',
          innerTopHeight: 270,
          isVisible: promoCardsVisibility?.saveTheDates !== false,
          primaryCtaLabel: translations.promoCards.saveTheDates.primaryCta,
          primaryCtaLink: buildPath('cards/wedding/save-the-date')
        },
        dashboardStats: [
          [
            { id: 'printsOrdered', value: printSaveTheDates, type: 'default', icon: <Shipping />, link: buildPath('cards/wedding/save-the-date') },
            { id: 'digitalCardsSent', value: accordionData.saveTheDatesECards.length, type: 'default', icon: <Send />, link: buildPath('ecard/savethedate') }
          ]
        ],
        accordionMenuItems: [
          { id: 'contactCollector', icon: <MailingAddress size={'xl'} />, link: buildPath(adminRoutePaths.contact.path) },
          { id: 'guestList', icon: <GuestList size={'xl'} />, link: buildPath(adminRoutePaths.guests.path) },
          { id: 'designAndSend', icon: <SaveTheDate size={'xl'} />, link: buildPath('cards/wedding/save-the-date') }
        ]
      },
      {
        id: 'registry',
        accentColor: 'accentBlue5',
        icon: <Gift />,
        isEnabled: !accordionData.eventByName?.productVerticals?.optOutOfRegistry,
        showBadge: true,
        promoCard: {
          title: translations.promoCards.registry.title,
          subtitle: translations.promoCards.registry.subtitle,
          showDismissButton: true,
          backgroundImageUrl: 'https://withjoy.com/assets/public/dashboard/v3-promo-cards/registry_dash_card@2x.jpg',
          innerTopHeight: 260,
          isVisible: promoCardsVisibility?.registry !== false,
          primaryCtaLabel: translations.promoCards.registry.primaryCta,
          primaryCtaLink: buildPath(adminRoutePaths.registry.path + '/' + registryRoutePaths.catalog.path),
          secondaryCtaLabel: translations.promoCards.registry.secondaryCta,
          secondaryCtaLink: buildPath(adminRoutePaths.registry.path + '/' + registryRoutePaths.manage.path) + '?linkRegistryDialogOpen=true'
        },
        statusBadgeItems: [
          {
            label: getStatusBadgeItemLabel(accordionData.eventByName?.pages?.find(page => page.pageSlug === EventPageSlugs.Registry)?.visibility, translations.badges.registry),
            icon: getStatusBadgeIcon(accordionData.eventByName?.pages?.find(page => page.pageSlug === EventPageSlugs.Registry)?.visibility),
            pageSlug: EventPageSlugs.Registry
          }
        ],
        dashboardStats: [
          [
            {
              id: 'giftsAvailable',
              value: accordionData.eventByName?.registry.itemCount.available || 0,
              type: 'default',
              icon: <Gift />,
              link: buildPath(adminRoutePaths.registry.path + '/' + registryRoutePaths.manage.path) + '?filter=stillNeeded'
            },
            {
              id: 'reservedAndPurchased',
              value: accordionData.eventByName?.registry.itemCount.purchasedOrReserved || 0,
              type: 'default',
              icon: <CheckOutline />,
              link: buildPath(adminRoutePaths.registry.path + '/' + registryRoutePaths.manage.path) + '?filter=reservedAndPurchased'
            },
            {
              id: 'thankYouNotesSent',
              value: accordionData.getMessageRelationships.length || 0,
              type: 'default',
              icon: <Send />,
              link: buildPath(adminRoutePaths.registry.path + '/' + registryRoutePaths.track.path)
            }
          ],
          [
            {
              id: 'cashReceived',
              value: cashReceivedInMinorUnits,
              total: goalInMinorUnits,
              type: 'progress',
              icon: <CashFunds />,
              link: buildPath(adminRoutePaths.registry.path + '/' + registryRoutePaths.manage.path) + '?filter=purchased'
            }
          ]
        ],
        accordionMenuItems: [
          { id: 'overview', icon: <ExternalRegistry size={'xl'} />, link: buildPath(adminRoutePaths.registry.path + '/' + registryRoutePaths.home.path) },
          { id: 'addGifts', icon: <Bag size={'xl'} />, link: buildPath(adminRoutePaths.registry.path + '/' + registryRoutePaths.catalog.path) },
          { id: 'manage', icon: <Gift size={'xl'} />, link: buildPath(adminRoutePaths.registry.path + '/' + registryRoutePaths.manage.path) },
          { id: 'trackAndThanks', icon: <Shipping size={'xl'} />, link: buildPath(adminRoutePaths.registry.path + '/' + registryRoutePaths.track.path) }
        ]
      },
      {
        id: 'invitations',
        accentColor: 'accentGreen3',
        icon: <Invitation />,
        showBadge: true,
        isEnabled: !accordionData.eventByName?.productVerticals?.optOutOfInvites,
        promoCard: {
          title: translations.promoCards.invitations.title,
          subtitle: translations.promoCards.invitations.subtitle,
          showDismissButton: true,
          backgroundImageUrl: 'https://withjoy.com/assets/public/dashboard/v3-promo-cards/invitation_dash_card@2x.jpg',
          innerTopHeight: 270,
          isVisible: promoCardsVisibility?.invitations !== false,
          primaryCtaLabel: translations.promoCards.invitations.primaryCta,
          primaryCtaLink: buildPath('cards/wedding/invitation')
        },
        dashboardStats: [
          [
            { id: 'printsOrdered', value: printInvitations, type: 'default', icon: <Shipping />, link: buildPath('cards/wedding/invitation') },
            { id: 'digitalCardsSent', value: accordionData.invitationsECards.length, type: 'default', icon: <Send />, link: buildPath('ecard/invitation') }
          ]
        ],
        accordionMenuItems: [
          { id: 'contactCollector', icon: <MailingAddress size={'xl'} />, link: buildPath(adminRoutePaths.contact.path) },
          { id: 'guestList', icon: <GuestList size={'xl'} />, link: buildPath(adminRoutePaths.guests.path) },
          { id: 'designAndSend', icon: <Invitation size={'xl'} />, link: buildPath('cards/wedding/invitation') }
        ]
      },
      {
        id: 'contactGuests',
        accentColor: 'brandPurple4',
        icon: <Send />,
        showBadge: false,
        accordionMenuItems: [
          { id: 'sendMessageToGuests', icon: <Envelope size={'xl'} />, link: buildPath(adminMonorepoRoutePaths.emailCreate) + '?draftType=generic&fromDashboard=true' },
          { id: 'remindGuestsToRSVP', icon: <AlarmClock size={'xl'} />, link: buildPath(adminMonorepoRoutePaths.emailCreate) + '?draftType=reminder&fromDashboard=true' },
          { id: 'sendBookHotelReminder', icon: <HotelProduct size={'xl'} />, link: buildPath(adminRoutePaths.hotelReminder.path) },
          { id: 'thankYouCards', icon: <Stationery size={'xl'} />, link: buildPath(adminRoutePaths.card.path) + '?ctgy=thankYou' },
          { id: 'customCard', icon: <CustomCard size={'xl'} />, link: buildPath(adminRoutePaths.ecard.goToPath('custom')) }
        ]
      }
    ];

    if (isHotelBlockEnabled) {
      items.splice(3, 0, {
        id: 'travel',
        accentColor: 'linkHover',
        icon: <HotelProduct />,
        showBadge: false,
        promoCard: {
          title: translations.promoCards.travel.title,
          subtitle: translations.promoCards.travel.subtitle,
          showDismissButton: true,
          backgroundImageUrl: 'https://withjoy.com/assets/public/dashboard/v3-promo-cards/travel_dash_card_v2@2x.jpg',
          innerTopHeight: 280,
          isVisible: promoCardsVisibility?.travel !== false,
          primaryCtaLabel: translations.promoCards.travel.primaryCta,
          primaryCtaLink: buildPath(adminRoutePaths.hotelBlock.path)
        },
        dashboardStats: [
          [
            {
              id: 'hotelWishList',
              value: JSON.parse(localStorage.getItem('HOTEL_SELECTION_LOCAL_STORAGE_KEY') || '[]').length,
              type: 'default',
              icon: <Star />,
              link: buildPath(adminRoutePaths.hotelBlock.path)
            },
            { id: 'roomsRequested', value: +(accordionData.eventByName?.hotelRoomBlocks?.numberOfRooms || 0), type: 'default', icon: <Bed /> }
          ]
        ],
        accordionMenuItems: [
          { id: 'roomBlocks', icon: <Bed size={'xl'} />, link: buildPath(adminRoutePaths.hotelBlock.path) },
          { id: 'travelPage', icon: <Plane size={'xl'} />, link: buildPath(adminRoutePaths.travel.path) }
        ]
      });
    }

    return items;
  }, [
    accordionData,
    buildPath,
    getStatusBadgeIcon,
    getStatusBadgeItemLabel,
    handlePromoCardSecondaryCtaClick,
    isHotelBlockEnabled,
    promoCardsVisibility,
    translations,
    translationsFns,
    adminWebsiteOverviewExperimentEnabled,
    photosCollectionExperimentEnabled
  ]);

  const transitions = useTransition(accordionItems, item => item.id, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    trail: 120,
    config: {
      easing: cubicBezierEasingFn
    }
  });

  if (loadingRegistryOrdersIds || accordionDataLoading || !accordionItems) {
    return (
      <DashboardAccordionWrapper __css={styles.DashboardAccordionWrapper}>
        {[1, 2, 3, 4, 5, 6, 7].map(i => (
          <AccordionItem.Skeleton key={i} />
        ))}
      </DashboardAccordionWrapper>
    );
  }

  return (
    <>
      <DashboardAccordionWrapper __css={styles.DashboardAccordionWrapper}>
        {transitions.map(({ item, key, props }) => (
          <animated.div key={key} style={{ width: '100%', ...props }}>
            <AccordionItem
              label={translations.items[item.id]}
              icon={item.icon}
              accentColor={item.accentColor}
              showBadge={item.showBadge}
              badgeLabel={item.badgeLabel}
              onBadgeClick={() => handleAccordionBadgeClick(item.id)}
              isEnabled={item.isEnabled}
              onToggle={isOpen => handleAccordionItemToggleClick(isOpen, item.id)}
              renderPromoCard={() =>
                item.promoCard && (
                  <PromoCard
                    onPrimaryCtaClick={() => handlePromoCardPrimaryCtaClick(item.id)}
                    onSecondaryCtaClick={() => handlePromoCardSecondaryCtaClick(item.id)}
                    onDismissClick={() => handlePromoCardDismissClick(item.id)}
                    {...item.promoCard}
                  />
                )
              }
              renderStatusBadge={() =>
                item.statusBadgeItems && (
                  <StatusBadge>
                    {item.statusBadgeItems.map(badgeItem => (
                      <PageSettingsDialogProvider
                        key={badgeItem.label}
                        eventId={eventId}
                        eventPageId={accordionData?.eventByName?.pages.find(page => page.pageSlug === badgeItem.pageSlug)?.id}
                        resource={translations.items[item.id]}
                      >
                        <StatusBadge.Item
                          label={badgeItem.label}
                          icon={badgeItem.icon}
                          onClick={() => handleStatusBadgeItemClick(item.id)}
                          link={badgeItem.link}
                          opensSettingsDialog={item.id !== 'website'}
                        />
                      </PageSettingsDialogProvider>
                    ))}
                  </StatusBadge>
                )
              }
              renderContent={() => (
                <>
                  {item.dashboardStats && (
                    <DashboardStat>
                      {item.dashboardStats.map((statRow, rowIndex) => (
                        <DashboardStat.Row key={'statRow' + rowIndex}>
                          {statRow.map(stat =>
                            stat.type === 'progress' ? (
                              <DashboardStat.ItemProgress
                                key={stat.id}
                                value={stat.value}
                                total={stat.total || 0}
                                color={item.accentColor}
                                icon={stat.icon}
                                cta={translations.statCards[stat.id].cta}
                                ctaHover={translations.statCards[stat.id].ctaHover}
                                goalLabel={translations.statCards.progressType.goal}
                                currencyCode={accordionData?.eventByName?.registry.currency?.code}
                                currencyPlaces={accordionData?.eventByName?.registry.currency?.places}
                                onClick={() => handleStatClick(stat.id)}
                                {...(stat.link && { link: stat.link })}
                              />
                            ) : (
                              <DashboardStat.ItemDefault
                                key={stat.id}
                                cta={translations.statCards[stat.id].cta}
                                ctaHover={translations.statCards[stat.id].ctaHover}
                                value={stat.value}
                                color={item.accentColor}
                                icon={stat.icon}
                                onClick={() => handleStatClick(stat.id)}
                                {...(stat.link && { link: stat.link })}
                              />
                            )
                          )}
                        </DashboardStat.Row>
                      ))}
                    </DashboardStat>
                  )}
                  <AccordionMenu>
                    {item.accordionMenuItems.map(menuItem => (
                      <AccordionMenu.Item
                        key={menuItem.id}
                        label={translations.menu.menuItems[menuItem.id]}
                        icon={menuItem.icon}
                        isNew={menuItem.isNew}
                        showChevronDown={menuItem.showChevronDown}
                        onClick={() => handleMenuItemClick(menuItem.id)}
                        {...(menuItem.link && { link: menuItem.link })}
                      />
                    ))}
                  </AccordionMenu>
                </>
              )}
            />
          </animated.div>
        ))}
      </DashboardAccordionWrapper>
      <PageListDialog
        eventPages={accordionData?.eventByName?.pages || []}
        productVerticals={accordionData?.eventByName?.productVerticals || {}}
        isOpen={isPageListDialogOpen}
        onToggle={onPageListDialogToggle}
        onClose={onPageListDialogClose}
      />
    </>
  );
};
